import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Users, Pencil, Trash2, ShieldCheck } from "lucide-react";

const EMPTY = { name: "", email: "", department: "Sales", designation: "Sales", role: "employee", password: "", status: "Existing" };

export default function Employees() {
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    const [u, d] = await Promise.all([api.get("/users"), api.get("/departments")]);
    setUsers(u.data); setDepts(d.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, department: u.department || "Sales", designation: u.designation || "Sales", role: u.role, password: "", status: u.status || "Existing" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, form);
        toast.success("Employee updated");
      } else {
        await api.post("/users", form);
        toast.success("Employee added (default password set)");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const del = async () => {
    await api.delete(`/users/${delTarget.id}`);
    toast.success("Employee deleted");
    setDelTarget(null); load();
  };

  return (
    <>
      <PageHeader
        title="Employees"
        subtitle="Manage staff accounts, departments and roles. New accounts get a default password."
        testId="employees-page"
        actions={
          <Button onClick={openNew} className="rounded-sm gap-2 font-bold uppercase text-xs tracking-wide" data-testid="add-employee-button">
            <Plus className="h-4 w-4" /> Add Employee
          </Button>
        }
      />

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        {users.length === 0 ? <div className="p-6"><Empty icon={Users} title="No employees" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-bold uppercase tracking-wide">Name</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wide hidden md:table-cell">Email</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wide">Department</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wide">Role</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wide text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="hover:bg-secondary/40" data-testid={`employee-row-${u.id}`}>
                    <TableCell className="font-semibold">{u.name}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="text-sm">{u.department} · <span className="text-muted-foreground">{u.designation}</span></TableCell>
                    <TableCell>
                      {u.role === "admin" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-primary"><ShieldCheck className="h-3.5 w-3.5" /> Admin</span>
                      ) : <span className="text-xs font-medium text-muted-foreground uppercase">Employee</span>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" onClick={() => openEdit(u)} data-testid={`edit-employee-${u.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {u.role !== "admin" && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-destructive" onClick={() => setDelTarget(u)} data-testid={`delete-employee-${u.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="employee-name-input" /></div>
            <div><Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" data-testid="employee-email-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Department</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="employee-dept-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Designation</Label>
                <Select value={form.designation} onValueChange={(v) => setForm({ ...form, designation: v })}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{["BD", "Sales", "MD"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="employee-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select></div>
              <div><Label>{editing ? "Reset password" : "Password"}</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editing ? "leave blank to keep" : "default: Veaimpex@123"} className="mt-1 rounded-sm" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!form.name || !form.email} className="rounded-sm" data-testid="save-employee-button">
              {editing ? "Save changes" : "Add employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Their customers will be unassigned. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="rounded-sm bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-employee">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
